import { useState, useEffect } from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Button,
  FormControlLabel,
  Checkbox,
  Chip,
} from "@mui/material";
import { Close as CloseIcon, LocationOn as LocationOnIcon } from "@mui/icons-material";
import entranceImg from "../../assets/naming_guidelines_3.png";

interface EntryPointNamingModalProps {
  open: boolean;
  onClose: () => void;
}

const styles = {
  dialogPaper: {
    borderRadius: "16px",
    padding: "24px 16px",
    width: "90%",
    maxWidth: "400px",
    margin: "16px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
  },
  title: {
    fontSize: "4.24vw",
    fontWeight: 700,
    color: "#191919",
    textAlign: "center" as const,
    flex: 1,
  },
  closeBtn: {
    padding: 0,
    color: "#000",
  },
  description: {
    fontSize: "3.29vw",
    color: "#666",
    marginBottom: "16px",
    textAlign: "center" as const,
  },
  imageCard: {
    position: "relative" as const,
    borderRadius: "12px",
    overflow: "hidden",
    marginBottom: "16px",
  },
  image: {
    width: "100%",
    height: "200px",
    objectFit: "cover" as const,
    display: "block",
  },
  imageOverlay: {
    position: "absolute" as const,
    bottom: 0,
    left: 0,
    right: 0,
    background: "#00000066",
    padding: "3%",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  overlayIcon: {
    color: "#fff",
    fontSize: "4.71vw",
  },
  overlayText: {
    color: "#fff",
    fontSize: "3.29vw",
    fontWeight: 600,
  },
  examplesHeader: {
    fontSize: "3.29vw",
    color: "#666",
    marginBottom: "8px",
  },
  examplesRow: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap" as const,
    marginBottom: "16px",
  },
  chip: {
    backgroundColor: "#fff",
    border: "1px solid #E0E0E0",
    color: "#191919",
    fontSize: "3.06vw",
  },
  checkboxLabel: {
    "& .MuiFormControlLabel-label": {
      fontSize: "3.06vw",
      color: "#666",
    },
  },
  button: {
    width: "100%",
    textTransform: "none" as const,
    color: "#fff",
    fontWeight: 600,
    fontSize: "3.76vw",
    padding: "12px",
    borderRadius: "30px",
    background: "linear-gradient(180deg, #9D79F4 0%, #7747FB 100%)",
    marginTop: "16px",
    "&:hover": {
      background: "linear-gradient(180deg, #7747FB 0%, #9D79F4 100%)",
    },
  },
};

export default function EntryPointNamingModal({
  open,
  onClose,
}: EntryPointNamingModalProps) {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  useEffect(() => {
    const isHidden = localStorage.getItem("hideEntryPointNamingGuidelines") === "true";
    setDontShowAgain(isHidden);
  }, [open]);

  const handleClose = () => {
    if (dontShowAgain) {
      localStorage.setItem("hideEntryPointNamingGuidelines", "true");
    } else {
      localStorage.removeItem("hideEntryPointNamingGuidelines");
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{ sx: styles.dialogPaper }}
    >
      <Box sx={styles.header}>
        <Box sx={{ width: 24 }} /> 
        <Typography sx={styles.title}>Naming Guidelines</Typography>
        <IconButton onClick={handleClose} sx={styles.closeBtn}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Typography sx={styles.description}>
        Describe the <strong>Entrance or Exit</strong> clearly using landmarks to help others find their way.
      </Typography>

      <Box sx={styles.imageCard}>
        <img src={entranceImg} alt="Entrance Example" style={styles.image} />
        <Box sx={styles.imageOverlay}>
          <LocationOnIcon sx={styles.overlayIcon} />
          <Typography sx={styles.overlayText}>
            Entrance Near A2B Restaurant
          </Typography>
        </Box>
      </Box>

      <Typography sx={styles.examplesHeader}>Examples</Typography>
      <Box sx={styles.examplesRow}>
        <Chip label="Entrance near KFC" sx={styles.chip} />
        <Chip label="Entrance Near Parking" sx={styles.chip} />
      </Box>

      <FormControlLabel
        control={
          <Checkbox
            checked={dontShowAgain}
            onChange={(e) => setDontShowAgain(e.target.checked)}
            size="small"
            sx={{
              color: "#8D28E6",
              "&.Mui-checked": {
                color: "#8D28E6",
              },
            }}
          />
        }
        label="Don't show again"
        sx={styles.checkboxLabel}
      />

      <Button sx={styles.button} onClick={handleClose}>
        Got it
      </Button>
    </Dialog>
  );
}
